/*
 * Copyright (c) CIRROM 2025.
 */

import { useState, useEffect } from "react";
import {
  getUserActivitySummary,
  getUserActivityByField,
  getUserActivityMonthly,
  getSummaryByFieldOverBatches,
  getContactProblemSummary,
  UserActivitySummary,
  UserActivityByField,
  UserMonthlyActivity,
  SummaryByFieldOverBatch,
  ContactProblemSummary,
} from "../../../api/dashboards";
import { IntegratedSystem } from "../../../api/integratedsystems";

export const useUserActivitySummary = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [userActivitySummary, setUserActivitySummary] = useState<
    UserActivitySummary[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject) {
      setIsLoading(true);
      getUserActivitySummary(systemOrProject.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setUserActivitySummary(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { userActivitySummary, isLoading };
};

export const useUserActivityByField = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [userActivityByField, setUserActivityByField] = useState<
    UserActivityByField[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject) {
      setIsLoading(true);
      getUserActivityByField(systemOrProject.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setUserActivityByField(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { userActivityByField, isLoading };
};

export const useUserActivityMonthly = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [userActivityMonthly, setUserActivityMonthly] = useState<
    UserMonthlyActivity[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject) {
      setIsLoading(true);
      getUserActivityMonthly(systemOrProject.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setUserActivityMonthly(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { userActivityMonthly, isLoading };
};

export const useSummaryByFieldOverBatches = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [summaryByFieldOverBatches, setSummaryByFieldOverBatches] = useState<
    SummaryByFieldOverBatch[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject) {
      setIsLoading(true);
      getSummaryByFieldOverBatches(systemOrProject.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setSummaryByFieldOverBatches(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { summaryByFieldOverBatches, isLoading };
};

export const useContactProblemSummary = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [contactProblemSummary, setContactProblemSummary] = useState<
    ContactProblemSummary[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject) {
      setIsLoading(true);
      getContactProblemSummary(systemOrProject.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setContactProblemSummary(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { contactProblemSummary, isLoading };
};
